import React, { useState, useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { formatDateTime } from '../../../utils/dateTime'

interface OptionItem {
  label: string;
  value: string;
}

interface OrderTemplatesListProps {
  role?: string;
  service: TableService; // New prop
  merchantOptions?: OptionItem[];
  salesChannelOptions?: OptionItem[];
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface OrderTemplateRow {
  id: string;
  name?: string;
  merchant_name?: string | null;
  sales_channel_names?: string;
  description?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

const OrderTemplatesList: React.FC<OrderTemplatesListProps> = ({
  role = 'admin',
  service, // New prop
  merchantOptions = [],
  salesChannelOptions = [],
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo<unknown[]>(() => {
    const cols: unknown[] = [
      {
        nameKey: `models.order_template.name`,
        persist: true,
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ id, name }: OrderTemplateRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/order_templates/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/order_templates/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `models.merchant.name`,
        persist: true,
        sortable: false,
        width: '150px',
        wrap: true,
        selector: ({ merchant_name }: OrderTemplateRow) => merchant_name || '-',
      });
    }

    cols.push(
      {
        name: (
          <Space size={4}>
            {t('models.order_template.sales_channel_names')}
            <Tooltip title={t('shared.order_templates.list.column_tooltips.sales_channel_names')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ sales_channel_names }: OrderTemplateRow) => {
          if (!sales_channel_names || sales_channel_names === '') {
            // 通路必填；未綁定通路時顯示 -
            return <span style={{ color: '#999' }}>-</span>
          }
          // Display multiple channels as tags
          const channels = sales_channel_names.split(', ');
          if (channels.length > 2) {
            return (
              <Space size={4} wrap>
                {channels.slice(0, 2).map((channel: string, idx: number) => (
                  <Tag key={idx}>{channel}</Tag>
                ))}
                <Tag>+{channels.length - 2}</Tag>
              </Space>
            );
          }
          return (
            <Space size={4} wrap>
              {channels.map((channel: string, idx: number) => (
                <Tag key={idx}>{channel}</Tag>
              ))}
            </Space>
          );
        },
      },
      {
        nameKey: `models.order_template.description`,
        sortable: false,
        width: '250px',
        wrap: true,
        selector: ({ description }: OrderTemplateRow) => description || '-',
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'order_templates.created_at',
        width: '180px',
        wrap: true,
        cell: ({ created_at }: OrderTemplateRow) => created_at ? formatDateTime(created_at) : '-',
      }
    );

    return cols;
  }, [role, urlPrefix, t, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t(`models.order_template.name`),
        component: MultipleValue,
        inputName: 'order_templates.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.order_templates.list.filter_tooltips.name.title'),
          content: t('shared.order_templates.list.filter_tooltips.name.content'),
        },
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`models.inventory.merchant_id`),
        component: MultipleValue,
        inputName: 'merchants.id',
        defaultPinned: true,
        createable: false,
        defaultOptions: merchantOptions,
        tooltip: {
          title: t('shared.order_templates.list.filter_tooltips.merchant.title'),
          content: t('shared.order_templates.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`models.order_template.sales_channel_id`),
        component: MultipleValue,
        inputName: 'order_templates.sales_channel_id',
        createable: false,
        defaultOptions: salesChannelOptions,
        tooltip: {
          title: t('shared.order_templates.list.filter_tooltips.sales_channel.title'),
          content: t('shared.order_templates.list.filter_tooltips.sales_channel.content'),
        },
      }
    );

    return rules;
  }, [role, t, merchantOptions, salesChannelOptions, hideMerchantUI])

  return (
    <TableIndex
      columns={columns}
      searchable={{ match: 'contains', searchFields: [t('models.order_template.name'), t('models.order_template.description')] }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}OrderTemplatesTable`}
      defaultQuery={{
        sorting_field: 'order_templates.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.order_templates.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/order_templates/list#features`}
    />
  )
}

export default OrderTemplatesList
